import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import defaultContent from "../../../content/downlineProfile";
import TabSwitcher from "../../../common/components/TabSwitcher/TabSwitcher";
import MetricsToolbar from "../Tabs/PerformanceMetrics/MetricsToolbar/MetricsToolbar";
import "./DownlineProfile.scss";
import AboProfileBanner from "../Header/ProfileBanner/AboProfileBanner";
import PerformanceMetrics from "../Tabs/PerformanceMetrics/PerformanceMetrics";
import KeyInformation from "../Tabs/KeyInformation/KeyInformation";
import Notes from "../Tabs/Notes/Notes";
import ExportButton from "../Export/ExportButton/ExportButton";
import { useAppDispatch, useAppSelector } from "../../../store";
import { PROFILE_TABS, ProfileTabIds, setActiveTab } from "../../../reducers/downlineProfile";
import { trackTabKeyInformation, trackTabNotes, trackTabPerformanceMetrics } from "./downlineProfileTealium";
import { SopStatus } from "../Tabs/SopStatus/SopStatus";
import { getSopStatusForAbo } from "../Tabs/SopStatus/sopStatusData";

type Props = {
  affAbo: string;
  pushToHistory?: (affAbo: string) => void;
};

const DownlineProfile = ({ affAbo: _affAbo, pushToHistory }: Props) => {
  const TAB_VIEWS: Record<ProfileTabIds, JSX.Element> = {
    [PROFILE_TABS.PERFORMANCE]: <PerformanceMetrics />,
    [PROFILE_TABS.SOP_STATUS]: <SopStatus />,
    [PROFILE_TABS.KEY_INFO]: <KeyInformation />,
    [PROFILE_TABS.NOTES]: <Notes key={_affAbo} />,
  };
  const dispatch = useAppDispatch();
  const { t } = useTranslation("downlineProfile");
  const { profile } = useAppSelector((store) => store.boot.configuration);
  const activeTab = useAppSelector((s) => s.downlineProfile.activeTab);
  const initialTab = useAppSelector((s) => s.profile.downlineProfileInitialTab);
  const sopStatus = getSopStatusForAbo(_affAbo);
  const showDownlineProfileExport =
    useAppSelector((s) => s.boot.configuration.profile?.showDownlineProfileExport as boolean) ?? true;
  const tabsScrollRef = useRef<HTMLDivElement>(null);

  const tabs = [
    {
      id: PROFILE_TABS.PERFORMANCE,
      text: t("tabs.performanceMetrics", defaultContent.tabs.performanceMetrics as string),
    },
    ...(profile.aboProfileTabs.includes(PROFILE_TABS.SOP_STATUS)
      ? [
          {
            id: PROFILE_TABS.SOP_STATUS,
            text: t("tabs.sopStatus", defaultContent.tabs.sopStatus as string),
            notification: sopStatus.kpis.productsOnSop,
          },
        ]
      : []),
    {
      id: PROFILE_TABS.KEY_INFO,
      text: t("tabs.keyInformation", defaultContent.tabs.keyInformation as string),
    },
    { id: PROFILE_TABS.NOTES, text: t("tabs.notes", defaultContent.tabs.notes as string) },
  ];

  useEffect(() => {
    dispatch(setActiveTab(initialTab ?? PROFILE_TABS.PERFORMANCE));
  }, [dispatch, initialTab]);

  useEffect(() => {
    const scrollContainer = tabsScrollRef.current;
    const tabElement = scrollContainer?.querySelector<HTMLElement>(`#TabSwitcher_${activeTab}`);
    if (!scrollContainer || !tabElement) return;

    const SCROLL_GAP = 8;
    const containerRect = scrollContainer.getBoundingClientRect();
    const tabRect = tabElement.getBoundingClientRect();
    const overflowRight = tabRect.right + SCROLL_GAP - containerRect.right;
    const overflowLeft = containerRect.left - (tabRect.left - SCROLL_GAP);

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const behavior: ScrollBehavior = reducedMotion ? "auto" : "smooth";

    if (overflowRight > 0) {
      scrollContainer.scrollBy({ left: overflowRight, behavior });
    } else if (overflowLeft > 0) {
      scrollContainer.scrollBy({ left: -overflowLeft, behavior });
    }
  }, [activeTab]);

  return (
    <div className="downline-profile" data-testid="downline-profile">
      <div className="downline-profile__banner" data-testid="downline-profile-banner" aria-live="polite">
        <div className="downline-profile__banner__nav-buttons"></div>
        <AboProfileBanner pushToHistory={pushToHistory} />
      </div>
      <div className="downline-profile__content" data-testid="downline-profile-content">
        <div className="downline-profile__header">
          <div className="downline-profile__tabs" ref={tabsScrollRef}>
            <TabSwitcher
              Tabs={tabs.filter((tab) => profile.aboProfileTabs.includes(tab.id))}
              selectedTab={activeTab}
              onTabClick={(id) => {
                dispatch(setActiveTab(id as ProfileTabIds));
                switch (id) {
                  case PROFILE_TABS.PERFORMANCE:
                    trackTabPerformanceMetrics();
                    break;
                  case PROFILE_TABS.KEY_INFO:
                    trackTabKeyInformation();
                    break;
                  case PROFILE_TABS.NOTES:
                    trackTabNotes();
                    break;
                }
              }}
            />
          </div>
          <div className="downline-profile__toolbar">
            {activeTab === PROFILE_TABS.PERFORMANCE && <MetricsToolbar />}
            {showDownlineProfileExport && <ExportButton />}
          </div>
        </div>

        <div className="downline-profile__body" data-testid="downline-profile-body">
          {TAB_VIEWS[activeTab]}
        </div>
      </div>
    </div>
  );
};

export default DownlineProfile;
